// Repris du mécanisme ODB (translation-validation.ts), regex étendue aux
// tokens {{name}} : les placeholders doivent être strictement identiques
const PLACEHOLDER_RE = /\{\{\w+\}\}|\{\w+\}|%[sd]/g;
/**
 * Valide une réponse de traduction avant tout stockage :
 * - toutes les clés demandées sont présentes ;
 * - aucune clé arbitraire n'a été ajoutée ;
 * - chaque valeur est une chaîne ;
 * - les placeholders sont préservés (aucun inventé, aucun perdu).
 *
 * La comparaison porte sur l'ensemble des placeholders, pas sur leur nombre
 * d'occurrences : une traduction correcte peut légitimement en répéter un pour
 * respecter l'accord grammatical. « {count} attempt{plural} remaining » devient
 * « {count} tentative{plural} restante{plural} » en français, l'accord portant
 * à la fois sur le nom et sur l'adjectif.
 *
 * Lève une erreur descriptive au premier problème rencontré.
 */
export function validateTranslation(source, translated) {
    const sourceKeys = Object.keys(source);
    const translatedKeys = Object.keys(translated);
    const translatedKeySet = new Set(translatedKeys);
    const sourceKeySet = new Set(sourceKeys);
    const missing = sourceKeys.filter((k) => !translatedKeySet.has(k));
    if (missing.length > 0) {
        throw new Error(`Missing keys: ${missing.join(", ")}`);
    }
    const extra = translatedKeys.filter((k) => !sourceKeySet.has(k));
    if (extra.length > 0) {
        throw new Error(`Extra keys: ${extra.join(", ")}`);
    }
    for (const [key, val] of Object.entries(translated)) {
        const problem = checkValue(source[key], val, key);
        if (problem)
            throw new Error(problem);
    }
}
/**
 * Contrôle une valeur traduite isolée. Retourne null si elle est valide.
 *
 * Exportée pour que la saisie manuelle d'une traduction depuis l'administration
 * soit soumise exactement à la même règle que la sortie du modèle : une
 * correction à la main ne doit pas pouvoir casser un placeholder.
 */
export function checkValue(sourceValue, val, key) {
    if (typeof val !== "string") {
        return `Value for key "${key}" is not a string`;
    }
    const srcPlaceholders = [...new Set(sourceValue.match(PLACEHOLDER_RE) ?? [])].sort();
    const tgtPlaceholders = [...new Set(val.match(PLACEHOLDER_RE) ?? [])].sort();
    if (JSON.stringify(srcPlaceholders) !== JSON.stringify(tgtPlaceholders)) {
        return `Placeholders modified for key "${key}": expected [${srcPlaceholders.join(", ")}], got [${tgtPlaceholders.join(", ")}]`;
    }
    return null;
}
/**
 * Variante tolérante de {@link validateTranslation}, pensée pour les gros
 * catalogues : un modèle finit statistiquement par se tromper sur une clé
 * (typiquement en inventant un placeholder ou en en oubliant une), et il serait
 * absurde de perdre les centaines de traductions correctes du même lot.
 *
 * Une clé oubliée par le modèle est traitée comme une valeur fautive, pas comme
 * une erreur structurelle : sur un lot de cent clés, un oubli isolé ne dit rien
 * de la qualité des quatre-vingt-dix-neuf autres. C'est l'appelant qui juge,
 * sur la proportion de rejets, si la réponse entière est inexploitable.
 *
 * Reste bloquante la seule clé *inventée* : une réponse qui répond à côté de la
 * question n'est pas une maladresse ponctuelle.
 *
 * Les clés écartées retombent sur la langue source côté client, et n'entrent
 * pas au snapshot : elles repartent au cycle de synchronisation suivant.
 */
export function partitionValidTranslations(source, translated) {
    const sourceKeys = Object.keys(source);
    const translatedKeySet = new Set(Object.keys(translated));
    const sourceKeySet = new Set(sourceKeys);
    const extra = Object.keys(translated).filter((k) => !sourceKeySet.has(k));
    if (extra.length > 0) {
        throw new Error(`Extra keys: ${extra.join(", ")}`);
    }
    const valid = {};
    const rejected = [];
    for (const key of sourceKeys) {
        if (!translatedKeySet.has(key)) {
            rejected.push({ key, reason: `Missing key "${key}" in AI response` });
        }
    }
    for (const [key, val] of Object.entries(translated)) {
        const problem = checkValue(source[key], val, key);
        if (problem) {
            rejected.push({ key, reason: problem });
        }
        else {
            valid[key] = val;
        }
    }
    return { valid, rejected };
}
//# sourceMappingURL=validation.js.map